import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Grid,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import {
  createPointRoom,
  deletePointRoom,
  updateRoomName,
  getRoomSettingImpact,
  getBranchRoomImpacts,
} from '../../../api/point';
import { FormInputText } from '../../../form-components/FormInputText';
import { errorMessages } from '../../../utils/methods';
import ClassroomEdit from './ClassroomEdit';
import CommonModalButton from '../../../form-components/CommonModalButton';
import {
  ROOM_TYPE,
  ROOM_TYPE_LABELS,
  DEFAULT_FLAG_FIELD,
  DEFAULT_TARGET_LABELS,
  DEFAULT_TARGET_SHORT_LABELS,
} from '../../../constants/roomTypes';

const ClassroomSettings = forwardRef(
  ({ roomListInfo, setRoomListInfo, branchId, getDetailCenter, branchDetailInfo }, ref) => {
    const { t } = useTranslation();
    const user = useSelector(state => state.user.user);
    const methods = useForm();
    const toast = useToast();
    const { handleSubmit, control, setError } = methods;

    const [roomName, setRoomName] = useState([]);

    // 추가 다이얼로그 상태
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // 삭제 확인 다이얼로그 상태
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roomToDelete, setRoomToDelete] = useState(null);

    // 수정 다이얼로그 상태
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [roomToEdit, setRoomToEdit] = useState(null);
    // 저장 전 확인이 필요한 내용. { mode, values, impact, defaultChanges } 이며 null 이면 닫힘
    const [saveConfirm, setSaveConfirm] = useState(null);
    // 강의실별 현재 설정 기준 영향(유형 불일치·정원 초과 예정 수업 수) — 카드 뱃지
    const [impacts, setImpacts] = useState({});

    /**
     * 이 저장으로 기본 강의실이 바뀌는 레슨구분을 찾는다.
     * 이미 다른 강의실이 기본으로 잡혀 있으면 그 이름과 함께 돌려준다.
     */
    const findDefaultTakeovers = (values, currentRoomId) =>
      Object.keys(DEFAULT_TARGET_SHORT_LABELS)
        .filter(target => values[DEFAULT_FLAG_FIELD[target]] === 'Y')
        .map(target => {
          const previous = (roomListInfo || []).find(
            room => room.roomId !== currentRoomId && room[DEFAULT_FLAG_FIELD[target]] === 'Y',
          );
          return previous ? { target, previousRoomName: previous.name } : null;
        })
        .filter(Boolean);

    const loadImpacts = async () => {
      if (!branchId) return;
      try {
        const list = await getBranchRoomImpacts(branchId, user.token);
        setImpacts(
          (list || []).reduce((acc, item) => ({ ...acc, [item.roomId]: item }), {}),
        );
      } catch (error) {
        // 뱃지는 보조 정보라 실패해도 화면을 막지 않는다
        setImpacts({});
      }
    };

    // ref를 통해 외부에서 모달 열기
    useImperativeHandle(ref, () => ({
      submitForm: () => setAddDialogOpen(true),
    }));

    // 추가 다이얼로그 닫기
    const handleAddClose = () => {
      setAddDialogOpen(false);
    };

    const applyRoomCreate = async values => {
      setIsLoading(true);
      try {
        await createPointRoom(values, branchId, user.token);
        toast.success(t('Classroom added'));
        getDetailCenter();
        loadImpacts();
        setAddDialogOpen(false);
      } catch (error) {
        toast.error(errorMessages(error));
      } finally {
        setIsLoading(false);
        setSaveConfirm(null);
      }
    };

    // 강의실 추가 저장 — 다른 강의실이 이미 기본이면 넘겨받을지 먼저 확인한다
    const handleAddSave = async values => {
      const defaultChanges = findDefaultTakeovers(values, null);
      if (defaultChanges.length > 0) {
        setSaveConfirm({ mode: 'create', values, impact: null, defaultChanges });
        return;
      }
      await applyRoomCreate(values);
    };

    // 삭제 다이얼로그 열기
    const handleDeleteClick = roomId => {
      setRoomToDelete(roomId);
      setDeleteDialogOpen(true);
    };

    // 삭제 다이얼로그 닫기
    const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
    };

    // 실제 삭제 실행
    const handleDeleteConfirm = async () => {
      if (roomToDelete) {
        try {
          await deletePointRoom(roomToDelete, user.token);
          toast.success(t('The classroom has been deleted'));
          setRoomListInfo(roomListInfo.filter(room => room.roomId !== roomToDelete));
        } catch (error) {
          toast.error(errorMessages(error));
        }
      }
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
    };

    const handleRoomNameChange = (index, newName) => {
      // Update the name of the specific room
      const updatedRoomName = [...roomName];
      updatedRoomName[index].roomName = newName;
      setRoomName(updatedRoomName);
    };

    // 수정 다이얼로그 열기
    const handleEditClick = (room, index) => {
      setRoomToEdit({ ...room, index });
      setEditDialogOpen(true);
    };

    // 수정 다이얼로그 닫기
    const handleEditCancel = () => {
      setEditDialogOpen(false);
      setRoomToEdit(null);
      setSaveConfirm(null);
    };

    const applyRoomUpdate = async payload => {
      try {
        await updateRoomName(payload, roomToEdit.roomId, user.token);
        toast.success(t('The classroom name updated'));
        getDetailCenter();
        loadImpacts();
      } catch (error) {
        toast.error(errorMessages(error));
      }
      setSaveConfirm(null);
      setEditDialogOpen(false);
      setRoomToEdit(null);
    };

    // 수정 저장 — 유형 변경이 예정 수업에 미치는 영향과 기본 강의실 이관을 먼저 확인받는다
    const handleEditSave = async values => {
      if (!roomToEdit) return;
      const defaultChanges = findDefaultTakeovers(values, roomToEdit.roomId);
      let impact = null;
      const settingChanged =
        values.roomType !== (roomToEdit.roomType || ROOM_TYPE.ALL) ||
        values.maxSession !== (roomToEdit.maxSession ?? null);
      if (settingChanged) {
        try {
          const result = await getRoomSettingImpact(
            roomToEdit.roomId,
            { roomType: values.roomType, maxSession: values.maxSession },
            user.token,
          );
          if (result?.mismatchedFutureClassCount > 0 || result?.overCapacityFutureClassCount > 0) {
            impact = result;
          }
        } catch (error) {
          toast.error(errorMessages(error));
          return;
        }
      }
      if (impact || defaultChanges.length > 0) {
        setSaveConfirm({ mode: 'edit', values, impact, defaultChanges });
        return;
      }
      await applyRoomUpdate(values);
    };

    const handleSaveConfirm = () => {
      if (!saveConfirm) return;
      if (saveConfirm.mode === 'create') {
        applyRoomCreate(saveConfirm.values);
        return;
      }
      applyRoomUpdate(saveConfirm.values);
    };

    useEffect(() => {
      // 첫 마운트 시 roomListInfo 가 아직 fetch 전이면 .map 이 터지므로 가드.
      if (!roomListInfo) return;
      setRoomName(roomListInfo.map(room => ({ roomName: room.name, roomId: room.roomId })));
    }, [roomListInfo]);

    useEffect(() => {
      if (!roomListInfo) return;
      loadImpacts();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomListInfo, branchId]);

    return (
      <Box>
        <hr style={{ border: '1px solid #e1e1e1' }} />
        <Box sx={{ mt: '30px' }}>
          {/* 강의실 카드 리스트 */}
          <Grid container spacing={2}>
            {roomListInfo &&
              roomListInfo.map((room, index) => {
                const impact = impacts[room.roomId];
                const hasWarning =
                  impact?.mismatchedFutureClassCount > 0 || impact?.overCapacityFutureClassCount > 0;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={room.roomId}>
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #ececec',
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        p: '16px',
                        transition: 'border-color .15s ease, box-shadow .15s ease',
                        '&:hover': {
                          borderColor: '#d5d5d5',
                          boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                        },
                      }}>
                      {/* 이름 + 유형 칩 */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', mb: '12px' }}>
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#262626',
                            wordBreak: 'break-all',
                          }}>
                          {room.name}
                        </Typography>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {Object.keys(DEFAULT_TARGET_SHORT_LABELS)
                            .filter(target => room[DEFAULT_FLAG_FIELD[target]] === 'Y')
                            .map(target => (
                              <Box
                                key={target}
                                sx={{
                                  px: '8px',
                                  py: '3px',
                                  borderRadius: '20px',
                                  backgroundColor: '#262626',
                                  color: '#fff',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}>
                                {t(DEFAULT_TARGET_SHORT_LABELS[target])}
                              </Box>
                            ))}
                          <Box
                            sx={{
                              px: '10px',
                              py: '3px',
                              borderRadius: '20px',
                              backgroundColor: '#E8F5F5',
                              color: '#2F8F97',
                              fontSize: '12px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}>
                            {t(ROOM_TYPE_LABELS[room.roomType || ROOM_TYPE.ALL])}
                          </Box>
                        </Box>
                      </Box>

                      {/* 동시간 최대 수업 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: hasWarning ? '10px' : 0 }}>
                        <Typography sx={{ fontSize: '13px', color: '#8c8c8c' }}>{t('동시간 최대 수업')}</Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#262626' }}>
                          {room.maxSession == null ? t('제한없음') : `${room.maxSession}${t('개')}`}
                        </Typography>
                      </Box>

                      {/* 예정 수업 경고 */}
                      {hasWarning && (
                        <Box
                          sx={{
                            backgroundColor: '#FFF4F1',
                            borderRadius: '8px',
                            p: '8px 10px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}>
                          {impact?.mismatchedFutureClassCount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <WarningIcon sx={{ fontSize: '14px', color: '#EE735A' }} />
                              <Typography sx={{ fontSize: '12px', color: '#D0503A' }}>
                                {t('유형과 맞지 않는 예정 수업')} {impact.mismatchedFutureClassCount}
                                {t('건')}
                              </Typography>
                            </Box>
                          )}
                          {impact?.overCapacityFutureClassCount > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <WarningIcon sx={{ fontSize: '14px', color: '#EE735A' }} />
                              <Typography sx={{ fontSize: '12px', color: '#D0503A' }}>
                                {t('최대 수업 수를 넘는 예정 수업')} {impact.overCapacityFutureClassCount}
                                {t('건')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* 액션 */}
                      <Box
                        sx={{
                          mt: 'auto',
                          pt: '12px',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '4px',
                        }}>
                        <Button
                          size="small"
                          onClick={() => handleEditClick(room, index)}
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#2F8F97',
                            minWidth: 'auto',
                            px: '10px',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: '#E8F5F5' },
                          }}>
                          {t('Edit')}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleDeleteClick(room.roomId)}
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#8c8c8c',
                            minWidth: 'auto',
                            px: '10px',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: '#FFF1EE', color: '#EE735A' },
                          }}>
                          {t('Delete')}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>

          <Box sx={{ mt: '30px' }}>
            <Box sx={{ display: 'flex', width: '1200px', maxWidth: '100%' }}>
              <Typography
                sx={{
                  mt: '10px',
                  color: '#9e9e9e',
                  lineHeight: '14.32px',
                  fontWeight: '500',
                  fontSize: '12px',
                  width: '340px',
                }}>
                {t('* If you delete a classroom, you cannot control its schedule in the calendar')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 삭제 확인 다이얼로그 */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          maxWidth="sm"
          fullWidth>
          <DialogTitle
            id="delete-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#f44336',
              textAlign: 'center',
            }}>
            <WarningIcon sx={{ mr: 1, color: '#f44336' }} />
            주의! 강의실을 삭제하면 복구할 수 없습니다.
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              해당 강의실에 연동된 모든 예약 및 결제 내역이 함께 삭제되며,
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              수강생 스케줄에 심각한 영향을 줄 수 있습니다.
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              정말 강의실을 삭제하시겠습니까?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              padding: '16px 24px',
              justifyContent: 'center',
            }}>
            <Button
              onClick={handleDeleteCancel}
              sx={{
                color: '#666',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}>
              취소
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#0F7F85',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#0a6b70',
                },
              }}>
              확인
            </Button>
          </DialogActions>
        </Dialog>

        {/* 저장 전 확인 — 기본 강의실 이관 / 유형 변경 영향 */}
        <Dialog open={!!saveConfirm} onClose={() => setSaveConfirm(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 700 }}>
            <WarningIcon sx={{ mr: 1, color: '#EE735A' }} />
            {saveConfirm?.defaultChanges?.length > 0 && !saveConfirm?.impact
              ? t('기본 강의실을 바꿀까요?')
              : t('확인이 필요합니다')}
          </DialogTitle>
          <DialogContent>
            {saveConfirm?.defaultChanges?.map(change => (
              <Typography key={change.target} variant="body1" sx={{ mb: 1 }}>
                {t(DEFAULT_TARGET_LABELS[change.target])} {t('기본 강의실이')}{' '}
                <strong>{change.previousRoomName}</strong>
                {t('에서 이 강의실로 바뀝니다.')}
              </Typography>
            ))}
            {saveConfirm?.impact?.mismatchedFutureClassCount > 0 && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t('이 강의실에 예정된 수업')} <strong>{saveConfirm.impact.mismatchedFutureClassCount}</strong>
                {t('건은 바꾸려는 유형과 맞지 않습니다.')}
                <br />
                {t('예정된 수업은 그대로 진행되지만, 앞으로 그 레슨구분에서는 이 강의실을 고를 수 없습니다.')}
              </Typography>
            )}
            {saveConfirm?.impact?.overCapacityFutureClassCount > 0 && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t('설정하려는 최대 수업 수를 이미 넘는 예정 수업이')} <strong>{saveConfirm.impact.overCapacityFutureClassCount}</strong>
                {t('건 있습니다. 해당 수업은 그대로 남지만, 강의실이나 시간을 바꾸는 수정은 막힙니다.')}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 2 }}>
              {t('이대로 저장할까요?')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={() => setSaveConfirm(null)}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSaveConfirm}>
              {t('저장')}
            </CommonModalButton>
          </DialogActions>
        </Dialog>

        {/* 강의실 추가 */}
        <ClassroomEdit
          open={addDialogOpen}
          onClose={handleAddClose}
          onSave={handleAddSave}
          isLoading={isLoading}
        />

        {/* 강의실 수정 — 추가와 같은 모달을 값만 채워 재사용한다 */}
        <ClassroomEdit
          open={editDialogOpen}
          mode="edit"
          onClose={handleEditCancel}
          onSave={handleEditSave}
          initialValues={
            roomToEdit
              ? {
                  roomName: roomToEdit.name,
                  roomType: roomToEdit.roomType,
                  maxSession: roomToEdit.maxSession,
                  defaultForPrivateYn: roomToEdit.defaultForPrivateYn,
                  defaultForGroupYn: roomToEdit.defaultForGroupYn,
                  defaultForConsultationYn: roomToEdit.defaultForConsultationYn,
                }
              : null
          }
        />
      </Box>
    );
  },
);

export default ClassroomSettings;
